import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Button, Dialog, Divider, DialogActions, DialogContent, DialogTitle, Checkbox,
  Tooltip, Box, Typography, Switch,
} from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useForm, FormProvider } from 'react-hook-form';
import Warning from '@mui/icons-material/Warning';
import CriticalIllness from './criticalIllness';

export default function CriticalIllnessDialog(props) {
  const {
    open, handleClose, title, save, formValues, isReadOnly,
  } = props;

  const criticalIllnessObject = useSelector((state) => state.scenario.criticalIllnessObject);
  const isNewScenario = useSelector((state) => state.navigator.isNewScenario);
  const includeProductCI = useSelector((state) => state.scenario.includeProductCI);
  const [isDefaultChecked, setDefaultChecked] = useState(false);
  const [isProductIncludedChecked, setProductIncludedChecked] = useState(true);
  const scenario = useSelector((state) => state.scenario.scenario);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [criticalFormValues, setCriticalFormValues] = useState(formValues);
  const methods = useForm({ defaultValues: criticalIllnessObject, mode: 'onBlur' });
  const [initialProductIncludedChecked, setInitialProductIncludedChecked] = useState(true);
  const { reset, handleSubmit, formState: { isValid } } = methods;

  useEffect(() => {
    reset(criticalIllnessObject);
    methods.trigger();
    let total = criticalIllnessObject.expectedEnrollment;
    for (let i = 2; i <= 5; i += 1) {
      total += criticalIllnessObject[`expectedEnrollmentOption${i}`] ?? 0;
    }
    methods.setValue('expectedEnrollmentTotal', total);
    if (open) {
      setDefaultChecked(criticalIllnessObject?.defaultSaveChecked);
      setProductIncludedChecked(isProductIncludedChecked);
      setInitialProductIncludedChecked(isProductIncludedChecked);
    } if (criticalIllnessObject.scenarioId === scenario.id) {
      setProductIncludedChecked(includeProductCI);
    }
  }, [criticalIllnessObject, open]);

  const getCheckedItems = () => {
    const checkedItems = [];
    if (criticalFormValues) {
      Object.keys(criticalFormValues).forEach((key) => {
        if (key.toLowerCase().includes('checked') && criticalFormValues[key]) {
          const itemName = key.replace(/checked/i, '');
          if (Number(criticalFormValues[itemName]) !== 0) {
            checkedItems.push({ name: itemName, value: criticalFormValues[itemName] });
            checkedItems.push({ name: key, value: criticalFormValues[key] });
          }
        }
      });
    }
    return checkedItems;
  };

  const handleSaveClick = () => {
    const item = getCheckedItems();
    if ((isProductIncludedChecked && item.length)
       || !isProductIncludedChecked || openCancelDialog) {
      if (isNewScenario) {
        handleSubmit((data) => save({
          ...data,
          defaultValueChecked: isDefaultChecked,
          defaultSaveChecked: isDefaultChecked,
        }, isProductIncludedChecked))();
      } else {
        handleSubmit((data) => save({
          ...data,
        }, isProductIncludedChecked))();
      }
    } else {
      setOpenCancelDialog(true);
    }
  };

  const handleCancelClick = () => {
    setProductIncludedChecked(initialProductIncludedChecked);
    handleClose();
  };

  return (
    <FormProvider {...methods}>
      <Dialog open={open} onClose={handleCancelClick} fullWidth maxWidth="xl">
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{title}</Typography>
            {!isReadOnly && (
              <Box display="flex" alignItems="center">
                <Typography variant="h6">Include Product</Typography>
                <Switch
                  size="medium"
                  checked={isProductIncludedChecked}
                  onChange={() => setProductIncludedChecked(!isProductIncludedChecked)}
                />
              </Box>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <CriticalIllness
            setCriticalFormValues={setCriticalFormValues}
            isDisabled={!isProductIncludedChecked || isReadOnly}
          />
        </DialogContent>
        <DialogActions>
          {isNewScenario && !isReadOnly && (
          <Box style={{ flex: '1', paddingLeft: '43px' }}>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={isDefaultChecked}
                  onChange={() => setDefaultChecked(!isDefaultChecked)}
                  disabled={!isProductIncludedChecked}
                />
              )}
              label="Save changes as my defaults"
            />
            <Tooltip
              style={{ margin: '0 0 -4px -11px' }}
              title="The user-saved defaults should apply the next time the user creates a scenario."
            >
              <InfoOutlinedIcon fontSize="small" />
            </Tooltip>
          </Box>
          )}
          {!isReadOnly && (
            <Button onClick={handleSaveClick} disabled={!isValid}>
              Save
            </Button>
          )}
          <Button onClick={handleCancelClick}>{isReadOnly ? 'Close' : 'Cancel'}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openCancelDialog} onClose={() => setOpenCancelDialog(false)}>
        <DialogTitle sx={{ fontSize: '1.25rem', color: 'primary.dark' }}>
          <Warning sx={{ marginBottom: '-5px' }} />
          &nbsp;
          No Projected Claims for product?
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ fontSize: '1rem', marginBottom: '1rem' }}>
          <Typography>
            To include this product on the output page,
            please select at least one benefit with projected claims.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenCancelDialog(false)}
            color="primary"
          >
            SELECT BENEFIT(S)
          </Button>
          <Button
            onClick={() => { handleSaveClick(); setOpenCancelDialog(false); }}
            color="primary"
            variant="contained"
          >
            SAVE WITHOUT BENEFIT(S)
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
